import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowRight, KeyRound, RefreshCw, Search, Settings2 } from 'lucide-react'
import { Shell, useShellMeta } from '../../admin/Shell.js'
import { SettingsCrumb } from '../../admin/SettingsCrumb.js'
import { Select, Tabs, usePermissions, type SelectOption } from '../../admin/primitives.js'
import { TextInput } from '../../admin/form.js'
import { guardedClose, useSaveBar } from '../../shell/editing.js'
import { useConfirm } from '../../shell/confirm.js'
import { Link } from '../../router.js'
import {
  STATUT_SOURCE, estListeSources, fmtDateHeure, lireSupervision, messageEchecMutation,
  type Lecture, type SondeSourceView,
} from '../api.js'
import { BandeauLecture, nomSalle, ordreSalle } from '../vues/commun.js'
import { LigneReglage } from './LigneReglage.js'
import { useUniteEdition, type UniteEdition } from './edition.js'
import { ecrireSonde } from './ecriture.js'
import {
  JSON_DEDIES, LIBELLE_PORTEE, corpsPut, correspond, dansOnglet, demandeStepUp, estRefPortee, estSchema, estValeurs,
  famillesDuSchema, fmtValeur, lirePortee, ongletsDuGroupe, porteesDuGroupe, reglagesDuGroupe,
  type GenrePortee, type GroupeSchema, type OptionSonde, type Portee, type ReglagePublie, type SchemaReglages, type ValeursReglages,
} from './modele.js'
import './reglages.css'

/**
 * ADMINISTRATION › SUPERVISION › <GROUPE> — UN ÉCRAN PAR GROUPE DU SCHÉMA
 * DE SONDE (§7.3, 21/10/2026).
 *
 * `/settings/supervision/<code du groupe>` : GET settings/schema + GET
 * settings?group=<code> (à la portée choisie) → une ligne par réglage, un
 * composant par type. Les onglets viennent du schéma (`groups[].tabs`).
 *
 * Save Bar : UNE unité d'édition pour l'écran (tous onglets confondus),
 * `If-Match` = la version du groupe lue, garde de navigation et Ctrl+S par le
 * provider, step-up proposé et rejoué. `onSave` rend `true` ou un TEXTE
 * d'erreur. Aucun bouton « Enregistrer » dans la page.
 *
 * Le groupe `source` se règle source par source (`PUT sources/<code>`,
 * `If-Match` = `settings_etag`) ; ses clés d'accès restent dans Intégrations.
 */

const SUCCES = 'Enregistré — Sonde le prend en compte en 10 secondes au plus.'

interface Zones { zones: { code: string; label: string }[] }
const estZones = (x: unknown): x is Zones => typeof x === 'object' && x !== null && Array.isArray((x as Zones).zones)
  && (x as Zones).zones.every((z) => typeof z?.code === 'string' && typeof z?.label === 'string')

const cheminValeurs = (groupe: string, portee: { kind: string; ref: string } | null) =>
  `/v/settings?group=${encodeURIComponent(groupe)}${portee ? `&scope_kind=${portee.kind}&scope_ref=${encodeURIComponent(portee.ref)}` : ''}`

function lirePorteeUrl(): Portee | null {
  try { return lirePortee(new URLSearchParams(window.location.search).get('portee')) } catch { return null }
}
function ecrirePorteeUrl(p: Portee | null) {
  try {
    const u = new URL(window.location.href)
    if (p) u.searchParams.set('portee', `${p.kind}:${p.ref}`); else u.searchParams.delete('portee')
    window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}${u.hash}`)
  } catch { /* adresse inchangée : sans gravité */ }
}

/** Le composant de route : lit le groupe, choisit l'écran. */
export function SupervisionGroupePage({ groupe }: { groupe: string }) {
  return groupe === 'source' ? <EcranSources /> : <EcranGroupe groupe={groupe} />
}

/* ═══════════════ Schéma + zones : ce que tous les écrans lisent ═══════════════ */

function useSchema() {
  const [schema, setSchema] = useState<Lecture<SchemaReglages> | null>(null)
  const recharger = useCallback(() => lireSupervision('/v/settings/schema', estSchema).then(setSchema), [])
  useEffect(() => { void recharger() }, [recharger])
  return { schema, recharger }
}

function useZones(actif: boolean): OptionSonde[] | null {
  const [zones, setZones] = useState<OptionSonde[] | null>(null)
  useEffect(() => {
    if (!actif) return
    let vivant = true
    void lireSupervision('/v/zones', estZones).then((l) => {
      if (vivant && l.etat === 'ok') setZones([...l.data.zones].sort((a, b) => ordreSalle(a.code) - ordreSalle(b.code)).map((z) => ({ value: z.code, label: nomSalle(z.code, z.label) })))
    })
    return () => { vivant = false }
  }, [actif])
  return zones
}

const besoinZones = (reglages: ReglagePublie[]) =>
  reglages.some((s) => s.scopes.includes('zone') || (JSON_DEDIES[s.key] && (JSON_DEDIES[s.key]!.genreCle === 'zone' || JSON_DEDIES[s.key]!.genreValeur.startsWith('zone'))))

/* ═══════════════ Écran d'un groupe ═══════════════ */

function EcranGroupe({ groupe }: { groupe: string }) {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const peutModifier = permissions.includes('sonde.settings.manage')
  const confirm = useConfirm()
  const { schema } = useSchema()
  const s = schema?.etat === 'ok' ? schema.data : null
  const g = s?.groups.find((x) => x.code === groupe) ?? null
  const reglages = useMemo(() => (s ? reglagesDuGroupe(s, groupe) : []), [s, groupe])
  const portees = useMemo(() => porteesDuGroupe(reglages), [reglages])

  const [portee, setPortee] = useState<Portee | null>(() => lirePorteeUrl())
  const [valeurs, setValeurs] = useState<Lecture<ValeursReglages> | null>(null)
  const charger = useCallback(async (p: Portee | null) => {
    const l = await lireSupervision(cheminValeurs(groupe, p), estValeurs)
    setValeurs(l)
    return l
  }, [groupe])
  useEffect(() => { void charger(portee) }, [charger, portee])

  const zones = useZones(besoinZones(reglages))
  const familles = useMemo(() => (s ? famillesDuSchema(s) : []), [s])
  const v = valeurs?.etat === 'ok' ? valeurs.data : null
  const unite = useUniteEdition(reglages, v?.values ?? null)
  const [motif, setMotif] = useState('')
  const [retour, setRetour] = useState<string | null>(null)

  const etag = v?.groups.find((x) => x.code === groupe)?.etag ?? g?.etag ?? null
  useSaveBar({
    dirty: peutModifier && unite.compte > 0,
    count: unite.compte,
    invalid: unite.corps.erreur,
    onSave: async () => {
      const c = unite.corps
      if (c.erreur) return c.erreur
      if (c.cles.length === 0) return true
      if (!etag) return 'Version des réglages inconnue : rechargez la page.'
      if (c.stepUp) {
        const sensibles = reglages.filter((r) => c.cles.includes(r.key))
          .filter((r) => demandeStepUp(r, unite.serveur.get(r.key)?.value, c.reset.includes(r.key) ? unite.serveur.get(r.key)?.default : c.values[r.key]))
        const ok = await confirm({
          title: sensibles.length > 1 ? `Modifier ${sensibles.length} réglages sensibles ?` : `Modifier « ${sensibles[0]?.label ?? 'ce réglage'} » ?`,
          message: sensibles.map((r) => r.consequence).filter(Boolean).slice(0, 2).join(' ') || 'Ce réglage est sensible : votre identité sera confirmée avant l’envoi.',
          details: sensibles.slice(0, 6).map((r) => ({
            label: r.label,
            value: `${fmtValeur(r, unite.serveur.get(r.key)?.value)} → ${fmtValeur(r, c.reset.includes(r.key) ? unite.serveur.get(r.key)?.default : c.values[r.key])}`,
          })),
          variant: 'sensitive',
          confirmLabel: 'Enregistrer',
        })
        if (!ok) return 'Enregistrement annulé — rien n’a été modifié.'
      }
      const r = await ecrireSonde('PUT', `settings/${encodeURIComponent(groupe)}`, corpsPut(c, portee, motif), etag)
      if (r.status === 409 && r.data?.error === 'PRECONDITION_FAILED') {
        await charger(portee)
        return 'Ces réglages ont été modifiés entre-temps dans Sonde : la page a relu la version actuelle et garde vos modifications. Vérifiez-les, puis enregistrez de nouveau.'
      }
      if (r.status >= 300) return messageEchecMutation(r.status, r.data)
      const n = c.cles.length
      await charger(portee)
      unite.vider(); setMotif('')
      setRetour(`${n} réglage${n > 1 ? 's' : ''} enregistré${n > 1 ? 's' : ''} et relu${n > 1 ? 's' : ''} dans Sonde à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })} : pris en compte en 10 secondes au plus, sans redémarrage.`)
      return true
    },
    onCancel: () => unite.vider(),
    successMessage: SUCCES,
  })

  const changerPortee = (p: Portee | null) => guardedClose(unite.compte > 0, () => {
    unite.vider(); setRetour(null); setPortee(p); ecrirePorteeUrl(p)
  }, 'Changer de portée abandonne les modifications en cours de cet écran.')

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-page--management lc-stagger sv-page sv-reg-page">
        <SettingsCrumb fallback="/settings/supervision" />
        <EnTeteGroupe g={g} groupe={groupe} nb={reglages.length} />
        {schema && schema.etat !== 'ok' ? <BandeauLecture lecture={schema} /> : null}
        {s && !g ? <p className="sv-bandeau sv-bandeau--gris" role="status">Sonde ne publie pas d’écran « {groupe} ». <Link href="/settings/supervision">Voir les écrans publiés</Link></p> : null}

        {g && portees.length > 0 ? (
          <ChoixPortee portees={portees} portee={portee} onChange={changerPortee} zones={zones} familles={familles} />
        ) : null}

        {valeurs && valeurs.etat !== 'ok' ? <BandeauLecture lecture={valeurs} /> : null}
        {!peutModifier && v ? <p className="lc-hint sv-reg-lecture-seule">Lecture seule : modifier ces réglages demande le droit « Réglages de la supervision ».</p> : null}
        {retour ? <p className="sv-bandeau sv-bandeau--ok" role="status">{retour}</p> : null}

        {g && v ? (
          <CorpsGroupe g={g} reglages={reglages} unite={unite} portee={portee} peutModifier={peutModifier}
                       ctx={{ zones }} motif={motif} setMotif={setMotif} />
        ) : g && !valeurs ? <p className="lc-hint sv-section">Lecture des réglages dans Sonde…</p> : null}

        {s ? <AutresEcrans groupes={s.groups} actuel={groupe} /> : null}
      </main>
    </Shell>
  )
}

function EnTeteGroupe({ g, groupe, nb, enPlus }: { g: GroupeSchema | null; groupe: string; nb: number; enPlus?: ReactNode }) {
  return (
    <header className="sv-reg-tete">
      <h1 className="lc-title cle-titre"><Settings2 size={22} aria-hidden="true" /> {g?.label ?? groupe}</h1>
      <p className="lc-subtitle">{g?.description ?? 'Réglages de Sonde.'}</p>
      <p className="sv-reg-tete__meta">
        {g ? <span>Écran {g.screen} · {nb} réglage{nb > 1 ? 's' : ''}</span> : null}
        <span>Source : schéma publié par Sonde</span>
        {enPlus}
      </p>
    </header>
  )
}

/* ─── Le corps : onglets, filtre, lignes, motif ─── */

function CorpsGroupe({ g, reglages, unite, portee, peutModifier, ctx, motif, setMotif, optionsPour }: {
  g: GroupeSchema; reglages: ReglagePublie[]; unite: UniteEdition; portee: Portee | null; peutModifier: boolean
  ctx: { zones: OptionSonde[] | null }; motif: string; setMotif: (v: string) => void
  optionsPour?: (cle: string) => OptionSonde[] | null
}) {
  // À une portée de surcharge, seuls les réglages qui l'acceptent (Sonde ne renvoie qu'eux).
  const visibles = reglages.filter((s) => unite.serveur.has(s.key))
  const onglets = ongletsDuGroupe(g, visibles)
  const [onglet, setOnglet] = useState<string>(() => {
    try {
      const o = new URLSearchParams(window.location.search).get('onglet')
      if (o !== null && onglets.some((x) => x.value === o)) return o
    } catch { /* onglet par défaut */ }
    return onglets[0]?.value ?? ''
  })
  const ongletActif = onglets.some((o) => o.value === onglet) ? onglet : onglets[0]?.value ?? ''
  const [filtre, setFiltre] = useState('')
  const filtrer = visibles.length > 12
  const lignes = visibles.filter((s) => (filtre.trim() ? correspond(s, filtre) : dansOnglet(s, ongletActif, g)))
  const modifiesParOnglet = (t: string) => visibles.filter((s) => dansOnglet(s, t, g) && unite.modifie(s)).length

  if (visibles.length === 0) {
    return <p className="sv-bandeau sv-bandeau--gris sv-section" role="status">Aucun réglage de cet écran ne se surcharge à cette portée.</p>
  }
  return (
    <section className="lc-panel sv-section sv-reg-panneau" aria-label={g.label}>
      {(onglets.length > 0 || filtrer) ? (
        <div className="sv-reg-outils">
          {onglets.length > 0 && !filtre.trim() ? (
            <div className="sv-reg-onglets">
              <Tabs tabs={onglets.map((o) => {
                const n = modifiesParOnglet(o.value)
                return { key: o.value, label: n > 0 ? `${o.label} · ${n}` : o.label }
              })} active={ongletActif} onChange={setOnglet} />
            </div>
          ) : null}
          {filtrer ? (
            <label className="sv-reg-filtre">
              <Search size={15} aria-hidden="true" />
              <TextInput value={filtre} onChange={setFiltre} placeholder="Chercher un réglage…" ariaLabel="Chercher un réglage" maxLength={80} />
            </label>
          ) : null}
        </div>
      ) : null}
      {lignes.length === 0 ? <p className="lc-hint sv-reg-vide">Aucun réglage ne correspond à « {filtre} ».</p> : (
        <ul className="sv-reg-lignes">
          {lignes.map((s) => (
            <LigneReglage key={s.key} s={s} serveur={unite.serveur.get(s.key)!} brouillon={unite.brouillon(s)}
                          modifie={unite.modifie(s)} remis={unite.edition.remises.includes(s.key)} erreur={unite.erreur(s)}
                          peutModifier={peutModifier} portee={portee}
                          ctx={{ zones: ctx.zones, ...(optionsPour ? { optionsPour } : {}) }}
                          onChange={(b) => unite.changer(s, b)} onRemettre={() => unite.remettre(s)} onAnnuler={() => unite.annuler(s)} />
          ))}
        </ul>
      )}
      {peutModifier && unite.compte > 0 ? (
        <div className="sv-reg-motif">
          <span className="lc-label">Motif (facultatif, gardé au journal de Sonde)</span>
          <TextInput value={motif} onChange={setMotif} maxLength={500} ariaLabel="Motif de la modification" placeholder="Pourquoi ce changement ?" />
        </div>
      ) : null}
    </section>
  )
}

/* ─── Portée : global, ou une surcharge par famille, salle ou appareil ─── */

function ChoixPortee({ portees, portee, onChange, zones, familles }: {
  portees: GenrePortee[]; portee: Portee | null; onChange: (p: Portee | null) => void
  zones: OptionSonde[] | null; familles: OptionSonde[]
}) {
  const [genre, setGenre] = useState<string>(portee?.kind ?? 'global')
  const [uid, setUid] = useState(portee?.kind === 'device' ? portee.ref : '')
  useEffect(() => { setGenre(portee?.kind ?? 'global'); if (portee?.kind === 'device') setUid(portee.ref) }, [portee])
  const genres: SelectOption[] = [{ value: 'global', label: 'Tout le club (valeur générale)' }, ...portees.map((k) => ({ value: k, label: `Surcharge : ${LIBELLE_PORTEE[k].toLowerCase()}` }))]
  const refs: OptionSonde[] = genre === 'zone' ? (zones ?? []) : genre === 'family' ? familles : []
  return (
    <section className="lc-panel sv-section sv-reg-portee" aria-label="Portée des réglages">
      <div className="lc-panel__body sv-reg-portee__corps">
        <div className="sv-reg-portee__champ">
          <span className="lc-label">Portée</span>
          <Select value={genre} options={genres} ariaLabel="Portée"
                  onChange={(k) => { setGenre(k); if (k === 'global') onChange(null) }} />
        </div>
        {genre === 'zone' || genre === 'family' ? (
          <div className="sv-reg-portee__champ">
            <span className="lc-label">{LIBELLE_PORTEE[genre]}</span>
            {refs.length > 0 ? (
              <Select value={portee?.kind === genre ? portee.ref : ''} placeholder="Choisir…" ariaLabel={LIBELLE_PORTEE[genre]}
                      options={refs.map((r) => ({ value: r.value, label: r.label }))}
                      onChange={(ref) => onChange({ kind: genre, ref })} />
            ) : <span className="lc-hint">{genre === 'zone' ? 'Salles de Sonde illisibles pour l’instant.' : '—'}</span>}
          </div>
        ) : null}
        {genre === 'device' ? (
          <div className="sv-reg-portee__champ">
            <span className="lc-label">Appareil (uid)</span>
            <span className="sv-reg-portee__uid">
              <TextInput value={uid} onChange={setUid} ariaLabel="uid de l’appareil" placeholder="ap-001-accueil" maxLength={200} />
              <button type="button" className="lc-btn" disabled={!estRefPortee(uid.trim())} onClick={() => onChange({ kind: 'device', ref: uid.trim() })}>Afficher</button>
            </span>
          </div>
        ) : null}
        <p className="lc-hint sv-reg-portee__aide">
          {portee
            ? `Vous réglez la surcharge « ${LIBELLE_PORTEE[portee.kind].toLowerCase()} ${portee.ref} » : elle l’emporte sur la valeur générale. « Retirer la surcharge » revient à la valeur générale.`
            : 'Vous réglez la valeur générale. Une surcharge par famille, salle ou appareil l’emporte sur elle.'}
        </p>
      </div>
    </section>
  )
}

function AutresEcrans({ groupes, actuel }: { groupes: GroupeSchema[]; actuel: string }) {
  return (
    <nav className="sv-section sv-reg-autres" aria-label="Autres écrans de la supervision">
      <span className="lc-label">Autres écrans</span>
      <ul>
        {[...groupes].sort((a, b) => a.screen - b.screen).filter((x) => x.code !== actuel).map((x) => (
          <li key={x.code}><Link className="sv-reg-autre" href={`/settings/supervision/${x.code}`}>{x.label}</Link></li>
        ))}
        {actuel !== 'journal' ? <li><Link className="sv-reg-autre" href="/settings/supervision/journal">Journal</Link></li> : null}
        {actuel !== 'consommation' ? <li><Link className="sv-reg-autre" href="/settings/supervision/consommation">Consommation de Sonde</Link></li> : null}
      </ul>
    </nav>
  )
}
export { AutresEcrans }

/* ═══════════════ Écran 1 : Sources, source par source ═══════════════ */

interface VueSource {
  code: string
  label: string
  status: keyof typeof STATUT_SOURCE
  status_since: string | null
  last_success_at: string | null
  last_error_code: string | null
  settings_etag: string
  read_posts: { op_id: string; label: string; allowed: boolean }[]
  gestion_integration_key: string | null
  credential: { configured: boolean; required: boolean }
}
const estVueSource = (x: unknown): x is VueSource => typeof x === 'object' && x !== null
  && typeof (x as VueSource).code === 'string' && typeof (x as VueSource).settings_etag === 'string'
  && Array.isArray((x as VueSource).read_posts)

function EcranSources() {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const peutModifier = permissions.includes('sonde.settings.manage')
  // La relève immédiate est le geste « synchroniser » de Gestion (droit du relais : sync.run).
  const peutActualiser = permissions.includes('sync.run')
  const confirm = useConfirm()
  const { schema } = useSchema()
  const s = schema?.etat === 'ok' ? schema.data : null
  const g = s?.groups.find((x) => x.code === 'source') ?? null
  const reglages = useMemo(() => (s ? reglagesDuGroupe(s, 'source') : []), [s])

  const [liste, setListe] = useState<Lecture<{ sources: SondeSourceView[] }> | null>(null)
  useEffect(() => { void lireSupervision('/v/sources', estListeSources).then(setListe) }, [])
  // Le SMTP se recopie depuis Communications : il ne se règle pas ici.
  const sources = useMemo(() => (liste?.etat === 'ok'
    ? liste.data.sources.filter((x) => x.code !== 'smtp').sort((a, b) => a.label.localeCompare(b.label, 'fr')) : []), [liste])
  const [code, setCode] = useState<string>(() => {
    try { return new URLSearchParams(window.location.search).get('source') ?? '' } catch { return '' }
  })
  const codeActif = code || sources[0]?.code || ''

  const [vue, setVue] = useState<Lecture<VueSource> | null>(null)
  const [valeurs, setValeurs] = useState<Lecture<ValeursReglages> | null>(null)
  const charger = useCallback(async (c: string) => {
    if (!c) return
    const [lv, lval] = await Promise.all([
      lireSupervision(`/v/sources/${encodeURIComponent(c)}`, estVueSource),
      lireSupervision(cheminValeurs('source', { kind: 'source', ref: c }), estValeurs),
    ])
    setVue(lv); setValeurs(lval)
  }, [])
  useEffect(() => { setVue(null); setValeurs(null); void charger(codeActif) }, [charger, codeActif])

  const v = valeurs?.etat === 'ok' ? valeurs.data : null
  const src = vue?.etat === 'ok' ? vue.data : null
  const unite = useUniteEdition(reglages, v?.values ?? null)
  const [motif, setMotif] = useState('')
  const [retour, setRetour] = useState<string | null>(null)
  const [actualise, setActualise] = useState<string | null>(null)

  useSaveBar({
    dirty: peutModifier && unite.compte > 0,
    count: unite.compte,
    invalid: unite.corps.erreur,
    onSave: async () => {
      const c = unite.corps
      if (c.erreur) return c.erreur
      if (c.cles.length === 0) return true
      if (!src) return 'Version de la source inconnue : rechargez la page.'
      if (c.stepUp) {
        const sensibles = reglages.filter((r) => c.cles.includes(r.key) && r.sensitive)
        const ok = await confirm({
          title: `Modifier ${src.label} ?`,
          message: sensibles.map((r) => r.consequence).filter(Boolean).slice(0, 2).join(' ') || 'Réglage sensible : votre identité sera confirmée avant l’envoi.',
          details: sensibles.slice(0, 6).map((r) => ({
            label: r.label,
            value: `${fmtValeur(r, unite.serveur.get(r.key)?.value)} → ${fmtValeur(r, c.reset.includes(r.key) ? unite.serveur.get(r.key)?.default : c.values[r.key])}`,
          })),
          variant: 'sensitive',
          confirmLabel: 'Enregistrer',
        })
        if (!ok) return 'Enregistrement annulé — rien n’a été modifié.'
      }
      const corps = corpsPut(c, null, motif)
      const r = await ecrireSonde('PUT', `sources/${encodeURIComponent(src.code)}`, corps, src.settings_etag)
      if (r.status === 409 && r.data?.error === 'PRECONDITION_FAILED') {
        await charger(src.code)
        return 'Cette source a été modifiée entre-temps dans Sonde : la page a relu la version actuelle et garde vos modifications. Vérifiez-les, puis enregistrez de nouveau.'
      }
      if (r.status >= 300) return messageEchecMutation(r.status, r.data)
      await charger(src.code)
      unite.vider(); setMotif('')
      setRetour(`${src.label} : réglages enregistrés et relus dans Sonde ; la source les applique à sa prochaine relève.`)
      return true
    },
    onCancel: () => unite.vider(),
    successMessage: SUCCES,
  })

  const choisir = (c: string) => guardedClose(unite.compte > 0, () => {
    unite.vider(); setRetour(null); setActualise(null); setCode(c)
    try {
      const u = new URL(window.location.href); u.searchParams.set('source', c)
      window.history.replaceState(window.history.state, '', `${u.pathname}${u.search}`)
    } catch { /* sans gravité */ }
  }, 'Changer de source abandonne les modifications en cours.')

  const actualiser = async () => {
    if (!src) return
    setActualise('Relève demandée…')
    const r = await ecrireSonde('POST', `sources/${encodeURIComponent(src.code)}/refresh`, {})
    if (r.status === 429) { setActualise('Une relève vient d’être demandée : attendre 15 secondes avant de recommencer.'); return }
    if (r.status >= 300) { setActualise(messageEchecMutation(r.status, r.data, 'aucune relève demandée')); return }
    setActualise('Relève demandée : l’état se met à jour dans quelques secondes.')
    setTimeout(() => { void charger(src.code) }, 4000)
  }

  // Lectures par POST : seulement celles que cette source connaît.
  const optionsPour = (cle: string): OptionSonde[] | null =>
    cle === 'source.read_posts_allowed' && src ? src.read_posts.map((o) => ({ value: o.op_id, label: o.label })) : null
  const statut = src ? STATUT_SOURCE[src.status] ?? { texte: src.status, pill: 'lc-pill--idle' } : null

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-page--management lc-stagger sv-page sv-reg-page">
        <SettingsCrumb fallback="/settings/supervision" />
        <EnTeteGroupe g={g} groupe="source" nb={reglages.length} />
        {schema && schema.etat !== 'ok' ? <BandeauLecture lecture={schema} /> : null}
        {liste && liste.etat !== 'ok' ? <BandeauLecture lecture={liste} /> : null}

        {sources.length > 0 ? (
          <section className="lc-panel sv-section sv-reg-portee" aria-label="Source réglée">
            <div className="lc-panel__body sv-reg-portee__corps">
              <div className="sv-reg-portee__champ">
                <span className="lc-label">Source</span>
                <Select value={codeActif} ariaLabel="Source" onChange={choisir}
                        options={sources.map((x) => ({ value: x.code, label: x.label }))} />
              </div>
              {src && statut ? (
                <div className="sv-reg-source">
                  <span className={`lc-pill ${statut.pill}`}>{statut.texte}</span>
                  <span className="lc-hint">
                    {/* `last_success_at` n'est écrit qu'au changement d'état chez Sonde : c'est « OK depuis », pas la dernière relève. */}
                    {src.status === 'OK'
                      ? (src.status_since ?? src.last_success_at ? `lue sans erreur depuis ${fmtDateHeure((src.status_since ?? src.last_success_at)!)}` : 'pas encore lue')
                      : src.status_since ? `dans cet état depuis ${fmtDateHeure(src.status_since)}` : '—'}
                    {src.last_error_code ? ` · dernier refus : ${src.last_error_code}` : ''}
                  </span>
                </div>
              ) : null}
              <div className="sv-reg-source__actions">
                <Link className="lc-btn" href="/settings/integrations/sonde">
                  <KeyRound size={14} aria-hidden="true" /> Clé d’accès : Intégrations › Sonde <ArrowRight size={14} aria-hidden="true" />
                </Link>
                {peutActualiser && src ? (
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => { void actualiser() }}>
                    <RefreshCw size={14} aria-hidden="true" /> Actualiser maintenant
                  </button>
                ) : null}
              </div>
              {actualise ? <p className="lc-hint" role="status">{actualise}</p> : null}
              <p className="lc-hint sv-reg-portee__aide">
                Les clés ne se saisissent jamais ici. Mode sûr : « Désactivée » ou « Lecture seule » seulement — Sonde ne sait que lire,
                les autres niveaux n’existent pas chez elle.
              </p>
            </div>
          </section>
        ) : null}

        {vue && vue.etat !== 'ok' ? <BandeauLecture lecture={vue} /> : null}
        {valeurs && valeurs.etat !== 'ok' ? <BandeauLecture lecture={valeurs} /> : null}
        {!peutModifier && v ? <p className="lc-hint sv-reg-lecture-seule">Lecture seule : modifier ces réglages demande le droit « Réglages de la supervision ».</p> : null}
        {retour ? <p className="sv-bandeau sv-bandeau--ok" role="status">{retour}</p> : null}

        {g && v && src ? (
          <CorpsGroupe g={g} reglages={reglages} unite={unite} portee={null} peutModifier={peutModifier}
                       ctx={{ zones: null }} motif={motif} setMotif={setMotif} optionsPour={optionsPour} />
        ) : null}

        {s ? <AutresEcrans groupes={s.groups} actuel="source" /> : null}
      </main>
    </Shell>
  )
}
